/**
 * SectionBox — one section of a record screen (Vehicles, Jobs, Invoices).
 * Design system (Alex, 2026-10-24): the header is a shaded header `band`
 * across the top with a 2px `borderStrong` rule under it, the title in 20px
 * bold and the count in a badge at the right, so nobody mistakes it for
 * content. Everything under the rule (tabs, search and sort, the list) is the
 * body. Controls never sit in the band, except a section's own `actions`.
 *
 * `collapsible` makes the whole band one big button that opens and closes the
 * section (My Work): a chevron at the right says which way it goes, and the
 * rule only shows while the body is open.
 *
 * `onOpen` makes the title a button that opens what the section counts (the
 * location dashboard: a stage opens its work orders). With `collapsible` too,
 * the chevron becomes its own button beside it, never one inside the other.
 *
 * `color` is a colour that comes from data (a shop's own section colour):
 * it paints accents only — a 4px left edge, a light tint behind the band
 * (mixed with the theme ground) and the rule under it. The title, count and
 * statuses stay on the theme tokens, so any colour reads in both themes.
 * `color={null}` keeps the 4px edge in the neutral border colour, so coloured
 * and uncoloured sections line up.
 */
import React from 'react';
import { DisclosureChevron } from './DisclosureChevron';
import { normalizeHexColor } from '@turn-connect/shared';
import {
  compactSpec,
  compactVars,
  sectionColorSpec,
  sectionSpec,
  textRole,
  tokenVars,
  type AccentKey,
  type Density,
} from '@turn-connect/ui';
import { AccentIcon } from './AccentIcon';
import { useDensity } from './density';

export interface SectionBoxCollapse {
  open: boolean;
  onToggle: () => void;
  /** Test id of the band's button (the chevron's, with `onOpen`). */
  testId?: string;
  /**
   * What a screen reader says for the band, instead of its title, hint and
   * badge ("Needs attention, 3 items"); `aria-expanded` adds open or closed.
   * Ignored with `onOpen` (the chevron says "Show …" / "Hide …").
   */
  label?: string;
}

export interface SectionBoxProps {
  title: string;
  /** Shown in a badge at the right of the header band. */
  count?: number;
  /** Words for the badge instead of the bare number ("2 new"). */
  countLabel?: string;
  /** `warning` tints the badge: something unread waits in the section. */
  countTone?: 'warning';
  /** The part's icon and color, matching its stat tile. */
  accent?: AccentKey;
  /** A ready-made icon for the band when the part has no shared accent (a report's own icon). Ignored when `accent` is set. */
  icon?: React.ReactNode;
  /** One short line under the title in the band ("Cars with your services still open"). */
  hint?: string;
  /** Band opens and closes the section. */
  collapsible?: SectionBoxCollapse;
  /** The title opens what the section counts (its own list). */
  onOpen?: () => void;
  /** A colour from data (a shop's section colour): edge, band tint and rule only. `null`: the neutral edge. */
  color?: string | null;
  /** The section's own controls, in the band before the badge. */
  actions?: React.ReactNode;
  /** The section's test id; the title is `<testId>-title`, the badge `<testId>-count`. */
  testId: string;
  /** `compact` (Reports, tables): 32px icon, 18px title, tighter band and body. Defaults to the nearest DensityProvider, else `default`. */
  density?: Density;
  children?: React.ReactNode;
}

const S = sectionSpec;

/**
 * Hover and keyboard focus for a collapsible band (inline styles cannot express them).
 * A band hovers by its ground alone: App.css's global button:hover (80% opacity,
 * a 1px lift) would dim and nudge the whole band.
 */
// The colour's share of a coloured band comes from the theme sheet
// (--tc-section-tint, generateThemeCSS), with the dark share as a fallback.
const pct = (share: number) => `${Math.round(share * 100)}%`;
// A neutral band hovers a step darker (bandHover); a shop-coloured one keeps
// surfaceRaised, as it had before the header band token.
const BAND_CSS = `.tc-section-band:hover { background: ${tokenVars.bandHover} !important; }
.tc-section-band[data-colored]:hover { background: ${tokenVars.surfaceRaised} !important; }
.tc-section-band:focus-visible, .tc-section-open:focus-visible, .tc-section-toggle:focus-visible { outline: 2px solid ${tokenVars.focus}; outline-offset: -2px; }
.tc-section-toggle:hover { background: ${tokenVars.wash} !important; }
.tc-section-band:hover, .tc-section-open:hover, .tc-section-toggle:hover { opacity: 1; transform: none; }
@media (hover: hover) { .tc-section-open:hover .tc-section-title { text-decoration: underline; text-underline-offset: 3px; } }`;

const C = compactSpec;

/** A band's ground: the section colour's themed share over bgSubtle, or plain bgSubtle. */
export const bandBackground = (hex: string | null): string =>
  hex ? `color-mix(in srgb, ${hex} var(--tc-section-tint, ${pct(sectionColorSpec.bandTint.dark)}), ${tokenVars.bgSubtle})` : tokenVars.band;

/** A count badge's ground: solid under the amber, so a section colour behind it can't thin its contrast. */
export const countBadgeBackground = (tone?: 'warning'): string =>
  tone === 'warning' ? `color-mix(in srgb, ${tokenVars.warning} 15%, ${tokenVars.surface})` : tokenVars.surfaceRaised;

const bandStyle = (ruled: boolean, compact: boolean, hex: string | null): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: compact ? C.band.gap : 12,
  background: bandBackground(hex),
  borderBottom: ruled
    ? `${S.band.ruleWidth}px solid ${hex ? `color-mix(in srgb, ${hex} ${pct(sectionColorSpec.rule)}, ${tokenVars.borderStrong})` : tokenVars.borderStrong}`
    : // Explicitly none, never undefined: on a closed collapsible band (a <button>
      // with `border: 0`) an unset longhand lets React clear it back to the
      // browser's 2px outset button border, a dark line under the band.
      'none',
  padding: compact
    ? `${C.band.paddingVertical}px ${C.band.paddingHorizontal}px`
    : `${S.band.paddingVertical}px ${S.band.paddingHorizontal}px`,
});

/** The band's title: `sectionTitle`, a step smaller when compact. */
const titleStyle = (compact: boolean): React.CSSProperties => ({
  ...textRole('sectionTitle'),
  ...(compact ? { fontSize: C.band.titleFontSize, lineHeight: `${C.band.titleLineHeight}px` } : null),
});

/**
 * The box and band for a panel that cannot be a SectionBox (its title is a
 * React node, a menu inside must not be clipped, it nests under a page's own
 * heading): the same surface, border, radius, header band and rule, from the
 * same spec. Settings panels and policy cards use them.
 */
export const sectionFrameStyle: React.CSSProperties = {
  background: tokenVars.surface,
  border: `1px solid ${tokenVars.border}`,
  borderRadius: S.radius,
  minWidth: 0,
};

/** A panel's header band (with its rule); `compact` is the tighter band nested panels use. */
export const sectionBandStyle = (compact = true): React.CSSProperties => ({
  ...bandStyle(true, compact, null),
  // The frame's radius on the band's top corners (the frame does not clip, so menus inside can overflow it).
  borderTopLeftRadius: S.radius - 1,
  borderTopRightRadius: S.radius - 1,
});

/** A panel's title in its band. */
export const sectionTitleStyle = (compact = true): React.CSSProperties => ({ ...titleStyle(compact), margin: 0, minWidth: 0 });

/** A panel's body padding and gap. */
export const sectionBodyStyle = (compact = true): React.CSSProperties => ({
  padding: compact ? C.body.padding : S.body.padding,
  display: 'flex',
  flexDirection: 'column',
  gap: compact ? C.body.gap : S.body.gap,
  minWidth: 0,
});

const plainButton: React.CSSProperties = { border: 0, background: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer' };

export const SectionBox: React.FC<SectionBoxProps> = ({
  title,
  count,
  countLabel,
  countTone,
  accent,
  icon: iconNode,
  hint,
  collapsible,
  onOpen,
  color,
  actions,
  testId,
  density: ownDensity,
  children,
}) => {
  const compact = useDensity(ownDensity) === 'compact';
  const headingId = `${testId}-title`;
  const bodyId = `${testId}-body`;
  // A box with nothing under its band (an empty stage) is just the band, without the rule.
  const hasBody = React.Children.toArray(children).length > 0;
  const open = (collapsible ? collapsible.open : true) && hasBody;
  // Only a real hex colour reaches the style (a non-colour string must never become a background).
  const hex = normalizeHexColor(color);
  const edge = color !== undefined;
  const badge = count !== undefined ? <CountBadge count={count} label={countLabel} tone={countTone} testId={`${testId}-count`} /> : null;
  const titleBlock = (
    <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <span
        id={headingId}
        data-testid={headingId}
        className="tc-section-title"
        style={{ ...titleStyle(compact), overflowWrap: 'anywhere' }}
      >
        {title}
      </span>
      {hint && (
        <span data-testid={`${testId}-hint`} style={textRole('meta', hex ? 'secondary' : undefined)}>
          {hint}
        </span>
      )}
    </span>
  );
  const icon = accent ? <AccentIcon accent={accent} size={compact ? 'tile' : 'band'} testId={`${testId}-icon`} /> : iconNode;
  const chevron = <DisclosureChevron open={open} />;

  let band: React.ReactNode;
  if (onOpen) {
    // Title opens the section's list; the chevron (when collapsible) is its own button.
    band = (
      <div data-testid={`${testId}-head`} style={{ ...bandStyle(open, compact, hex), paddingLeft: compact ? 4 : 8, paddingRight: 8 }}>
        <style>{BAND_CSS}</style>
        <h2 style={{ margin: 0, minWidth: 0, flex: '1 1 auto', font: 'inherit' }}>
          <button
            type="button"
            className="tc-section-open"
            onClick={onOpen}
            style={{
              ...plainButton,
              display: 'flex',
              alignItems: 'center',
              gap: compact ? C.band.gap : 12,
              width: '100%',
              minHeight: compact ? compactVars.controlHeight : 44,
              padding: '4px 8px',
              borderRadius: 8,
              textAlign: 'left',
              boxSizing: 'border-box',
            }}
          >
            {icon}
            {titleBlock}
          </button>
        </h2>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {actions}
          {badge}
          {collapsible && (
            <button
              type="button"
              className="tc-section-toggle"
              onClick={collapsible.onToggle}
              aria-expanded={open}
              aria-controls={open ? bodyId : undefined}
              aria-label={`${open ? 'Hide' : 'Show'} ${title}`}
              data-testid={collapsible.testId}
              style={{
                ...plainButton,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: compactVars.controlHeight,
                height: compactVars.controlHeight,
                borderRadius: 8,
              }}
            >
              {chevron}
            </button>
          )}
        </span>
      </div>
    );
  } else if (collapsible) {
    band = (
      <h2 style={{ margin: 0 }}>
        <style>{BAND_CSS}</style>
        <button
          type="button"
          className="tc-section-band"
          data-colored={hex ? '' : undefined}
          onClick={collapsible.onToggle}
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          aria-label={collapsible.label}
          data-testid={collapsible.testId}
          style={{
            // The shorthand first: a later `border` would wipe the rule.
            border: 0,
            ...bandStyle(open, compact, hex),
            width: '100%',
            minHeight: compact ? C.band.collapsibleMinHeight : 64,
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {icon}
          {titleBlock}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {badge}
            {chevron}
          </span>
        </button>
      </h2>
    );
  } else {
    band = (
      <div data-testid={`${testId}-head`} style={bandStyle(open, compact, hex)}>
        {icon}
        <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <h2 id={headingId} data-testid={headingId} style={{ ...titleStyle(compact), margin: 0, minWidth: 0 }}>
            {title}
          </h2>
          {hint && (
            <span data-testid={`${testId}-hint`} style={textRole('meta', hex ? 'secondary' : undefined)}>
              {hint}
            </span>
          )}
        </span>
        {(actions || badge) && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {actions}
            {badge}
          </span>
        )}
      </div>
    );
  }

  return (
    <section
      aria-labelledby={headingId}
      data-testid={testId}
      data-density={compact ? 'compact' : undefined}
      data-color={hex ?? undefined}
      style={{
        background: tokenVars.surface,
        // Longhands throughout, so the 4px edge never fights a `border` shorthand.
        borderStyle: 'solid',
        borderColor: tokenVars.border,
        borderWidth: edge ? '1px 1px 1px 4px' : 1,
        ...(edge && hex ? { borderLeftColor: hex } : null),
        borderRadius: S.radius,
        overflow: 'hidden',
        minWidth: 0,
        // Never squashed by a flex parent: with overflow hidden its minimum
        // height would be 0, and the content would be cut off.
        flexShrink: 0,
      }}
    >
      {band}
      {open && (
        <div
          id={bodyId}
          data-testid={bodyId}
          style={{
            padding: compact ? C.body.padding : S.body.padding,
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? C.body.gap : S.body.gap,
            minWidth: 0,
          }}
        >
          {children}
        </div>
      )}
    </section>
  );
};

/** The count at the right of a section's header band. */
export const CountBadge: React.FC<{ count: number; label?: string; tone?: 'warning'; testId?: string }> = ({
  count,
  label,
  tone,
  testId,
}) => (
  <span
    data-testid={testId}
    data-tone={tone}
    style={{
      ...textRole('countBadge', tone === 'warning' ? tokenVars.warningText : undefined),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: S.countBadge.minWidth,
      height: S.countBadge.height,
      padding: `0 ${S.countBadge.paddingHorizontal}px`,
      borderRadius: 999,
      background: countBadgeBackground(tone),
      border: `1px solid ${tone === 'warning' ? tokenVars.warning : tokenVars.border}`,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
    }}
  >
    {label ?? count}
  </span>
);

export default SectionBox;
